// Pre-warm the voxel bare-room eigensolve as soon as the user moves
// past the room-design step.
//
// Rationale
// ---------
// The voxel eigensolve is the single most expensive step in the whole
// pipeline (tens of seconds to minutes, dominated by an iterative or
// direct sparse-eigenvalue extraction on a 100k-500k DOF Laplacian).
// Its inputs are determined ENTIRELY by the room geometry — the
// speakers, listener, and panels that come later don't change a single
// eigenpair.  So the eigensolve can — and should — be fired the
// instant the user commits to a room geometry, not when they later
// click "optimize" and find themselves staring at a spinner.
//
// The backend cache (`backend/voxel_cache.py`) makes the cache shared
// across processes and across the whole pipeline: once this pre-warm
// completes, every downstream consumer (modal_warm, freq_response,
// rt60, place_treatment, solve_listener / solve_speakers / solve_joint)
// for the same room geometry hits the disk cache and skips the
// eigensolve entirely.  We do the heavy work once, while the user is
// busy positioning speakers; treatment placement that used to take
// hundreds of seconds completes in seconds because the eigensolve is
// already on disk.
//
// In-flight dedup + warm memo
// ---------------------------
// The memo lives in `eigensolveWarmMemo.ts`, shared with jobs that compute
// the eigensolve themselves. Repeated triggers for the same geometry
// collapse onto one HTTP call.  Once a warm SUCCEEDS the entry is KEPT
// (not deleted) so its resolved promise doubles as a "this geometry is
// already warm" memo: subsequent callers for the same geometry resolve
// instantly instead of submitting another modal_warm job (those were
// eigensolve cache hits but still tied up the single solve worker and
// starved real solves).  A geometry signature change (the user adjusts
// dims / furniture, then comes forward again) clears the memo, so the new
// geometry re-warms; a failed warm drops its own entry so it can be retried.
//
// About to submit a job that computes the eigensolve anyway (a placement
// solve, the untreated evaluate)? Call `warmEigensolveVia` with that job
// instead of this -- a modal_warm next to it is pure overhead.

import { createJobRunner, geometrySignature } from '@roomtreatment/acoustics-client';
import { adminApi } from './adminApi';
import { docToScene } from '../components/room/state/serialize';
import type { EditorDoc } from '../components/room/state/editorState';
import type { ModalPipeline } from './adminTypes';
import {
  clearEigensolveWarm,
  eigensolveWarmFor,
  trackEigensolveWarm,
} from './eigensolveWarmMemo';

export { geometrySignature };

/**
 * Single entrypoint for "ensure the voxel eigensolve is warm for the
 * doc's current geometry".  Called from:
 *
 *   - The furniture step's "Set up speakers" click
 *     (`useStepFlow.tsx`'s armVoxelIfChanged), which is the only sanctioned
 *     trigger for INITIATING an eigensolve.
 *   - `useAutoSim` / `useModalViz` once voxel is armed — they wait on
 *     the in-flight promise but never start a new solve themselves.
 *
 * Invariants:
 *   - At most ONE in-flight solve at any time, ever.  Multiple callers
 *     for the same geometry share the same Promise (no double-submit).
 *   - When a caller requests a DIFFERENT geometry while a solve for an
 *     older one is in flight, the older entry is abandoned (cleared
 *     from the in-flight map).  The backend solve continues and its
 *     result lands in the disk cache — wasted compute, but harmless
 *     and potentially useful if the user reverts the geometry change.
 *     Server-side cancellation is a future improvement.
 *   - No-op for the analytical pipeline (the analytical solver is
 *     already fast; nothing to warm).
 *
 * Returns a promise that resolves when the cache is populated.  Errors
 * are surfaced via console.warn but never thrown — a failed pre-warm
 * degrades to "the next solve takes its full cold time", not a UI block.
 */
export function preWarmEigensolve(
  doc: EditorDoc, pipeline: ModalPipeline,
): Promise<void> {
  if (pipeline !== 'voxel') return Promise.resolve();

  const signature = geometrySignature(doc);
  const existing = eigensolveWarmFor(signature);
  if (existing) return existing;

  // Build a Scene with placeholder speakers/listener — the backend's
  // `_run_modal_warm_job` ignores both (the bare eigensolve is geometry-
  // only).  Using `docToScene` keeps the geometry plumbing in lockstep
  // with every other JobAction request.
  const scene = docToScene(doc);

  // Submit as a regular spawn-pool job, through the same bounded
  // submit/poll/retry contract as every other queued physics operation (a
  // transient 503 cannot resolve this memo early and make a later caller
  // launch a second eigensolve through a different path). No `fmax` on the
  // action — the backend uses its canonical BARE_EIGENSOLVE_FMAX_HZ so this
  // populates the same cache entry every other consumer (freq_response,
  // rt60, modal, solve_*, place_treatment) reads from.
  const runner = createJobRunner({
    api: adminApi,
    pollIntervalMs: 800,
    pollTimeoutMs: PREWARM_POLL_TIMEOUT_MS,
  });
  // A new geometry abandons stale warms (their jobs finish server-side and
  // still fill the cache); success keeps the entry as the "already warm"
  // marker so repeat calls (useAutoSim's post-treatment pass, useModalViz's
  // before/after, every live edit in guided mode) resolve instantly instead
  // of submitting another modal_warm -- those redundant cache-hit jobs once
  // starved the result page's "Measuring the treated room…" pass; failure
  // drops the entry so a later call can retry. See eigensolveWarmMemo.ts.
  return trackEigensolveWarm(
    signature,
    runner.run<void>(scene, { kind: 'modal_warm' }, pipeline),
    (err) => console.warn('[preWarmEigensolve] failed -- next solve will be cold:', err),
  );
}

// Bound each individual poll: without it a backgrounded tab on a dead
// socket hangs the fetch forever and the retry below never gets to fire
// (it only triggers on a *thrown* error, not a silent hang).
const PREWARM_POLL_TIMEOUT_MS = 15000;

/** Drop every in-flight pre-warm.  Use on doc reset / sign-out. */
export function clearPreWarmInflight(): void {
  clearEigensolveWarm();
}
